import type { Company, Contact, ContactNote } from "../types";

/**
 * Barrel Scout additions to Atomic CRM's types.
 *
 * WHY THESE LIVE HERE AND NOT IN ../types.ts
 * ------------------------------------------
 * This is a fork that keeps merging from marmelab/atomic-crm. Anything we put
 * in an upstream file conflicts at whatever rate upstream edits that file;
 * anything in our own directory never conflicts at all.
 *
 * Measured over the last 12 months of upstream history:
 *
 *     src/components/atomic-crm/root/CRM.tsx                     53 commits
 *     src/components/atomic-crm/companies/CompanyShow.tsx        18
 *     src/components/atomic-crm/types.ts                         14
 *     .../providers/fakerest/dataGenerator/companies.ts           9
 *     src/components/atomic-crm/companies/CompanyCreate.tsx       8
 *     src/components/atomic-crm/marketing/*                       0
 *
 * Adding `address_id` to their `Company` cost 14 commits/yr of conflict risk,
 * and forced a second edit to their demo data generator -- which types its
 * output as `Required<Company>`, so every optional field must be present --
 * for another 9. Declaring it here costs nothing and behaves identically.
 *
 * Keep this rule: new behavior goes in ../marketing/. Touch an upstream file
 * only where the framework genuinely requires an injection point, and keep that
 * edit to the fewest lines that work.
 */

/**
 * A company that may have been promoted from a door on the mailing list.
 *
 * `address_id` references `addresses(id)` in Postgres, added by
 * barrel_scout_crm/db/migrations/003_crm_link.sql. Null -- or absent -- for
 * companies that never came from a mailing, such as a phone lead or referral,
 * which is a normal state and not an error.
 *
 * Deliberately a bigint FK and never street text. The previous `address_id`
 * was a hash of the street that drifts when TTB re-types it, or when the set
 * of permits at a building changes.
 */
export type CompanyWithDoor = Company & {
  address_id?: number | null;
};

/**
 * One row of `public.engagements` -- a thing that actually happened with a
 * person. See barrel_scout_crm/db/migrations/048_lifecycle_core.sql.
 *
 * Channel-agnostic on purpose: a phone call, a sent email and a link click all
 * share this shape, so the history on a contact page is one list rather than
 * four that have to be mentally merged.
 *
 * The kind and source unions mirror the CHECK constraints in 048. They are
 * repeated here rather than generated because a generated type would silently
 * widen the moment someone adds a kind, and the point of writing them down is
 * that ./touchLog.ts has to be given a label for each one.
 */
export type EngagementKind =
  | "call"
  | "email"
  | "met"
  | "other"
  | "note"
  | "task"
  | "delivered"
  | "opened"
  | "clicked";

export type EngagementSource = "manual" | "note" | "task" | "resend" | "import";

export type Engagement = {
  id: number;
  contact_id: number;
  kind: EngagementKind;
  channel: string | null;
  /** When contact HAPPENED, not when it was recorded. Back-dateable. */
  occurred_at: string;
  note: string | null;
  source: EngagementSource;
  source_note_id: number | null;
  email_id: number | null;
  content_id: number | null;
  created_at: string;
};

/**
 * Upstream's Contact plus the lifecycle columns added by 048.
 *
 * All optional, because every one of them is nullable in Postgres -- upstream
 * inserts know nothing about them, which is the whole reason they are nullable
 * there and the reason they are optional here.
 *
 * `last_contacted_at` is DERIVED: exactly one database trigger writes it, as
 * max(engagements.occurred_at). Never write to it from the app.
 *
 * READ THIS BEFORE USING ANY FIELD BELOW ON A CONTACT PAGE.
 *
 * These columns exist on the `contacts` TABLE. They do NOT reach the record
 * that ContactShow renders. The data provider rewrites getOne/getList for
 * "contacts" to "contacts_summary", an upstream view that lists its columns
 * explicitly and predates 048, so it still returns its original 21 and gains
 * nothing when the table gains a column.
 *
 * The failure is silent and total: the field reads `undefined`, so a header
 * saying "Never contacted" renders for every contact in the database and looks
 * entirely deliberate. TouchHistory therefore derives "last contacted" from the
 * top of the engagement list instead, which is the same max(occurred_at) the
 * trigger computes.
 *
 * Widening the view (a `create or replace view` migration, as 057 did for the
 * three filter views) is the fix if any of these are ever needed on a page.
 * Until then, treat every field here as absent in the UI.
 */
export type ContactWithLifecycle = Contact & {
  lifecycle_stage?: string | null;
  track?: "prospect" | "partner" | null;
  stale_after_days?: number | null;
  commitment_at?: string | null;
  commitment_note?: string | null;
  paused_until?: string | null;
  paused_reason?: string | null;
  last_contacted_at?: string | null;
  source?: string | null;
  source_detail?: string | null;
};

/**
 * Upstream's ContactNote plus the opt-out flag added by 048.
 *
 * Optional, and DEFAULT TRUE in the database: an upstream insert that omits it
 * still records a touch, which is the behavior we want, because most notes do
 * record contact.
 */
export type ContactNoteWithTouch = ContactNote & {
  counts_as_touch?: boolean;
};

/**
 * One row of `public.contact_health` — the single source of "when is this
 * contact next due?". See db/migrations/062_contact_health_on_lifecycle.sql.
 *
 * A VIEW, not a table, and the only thing that should ever be READ for due
 * dates. Three accountability views and the Monday digest select from it, so
 * its shape is a contract: widening it to answer a UI question would change
 * what `select *` freezes into each of them.
 *
 * `id` IS the contact id — the view selects `c.id` — which is why the panel
 * can fetch it with `filter: { id: contact.id }` and why react-admin's own
 * record-identity contract is satisfied without any provider configuration.
 *
 * WHAT IS DELIBERATELY NOT HERE: which of the three cadence levels won.
 * `effective_stale_after` is a coalesce of the record override, the stage
 * default and a floor of 7, and the view keeps no record of the winner. The
 * panel derives that in JS from contact_lifecycle and lifecycle_stages —
 * see cadenceSource in ./lifecycleRules.ts.
 */
export type ContactHealth = {
  id: number;
  first_name: string | null;
  last_name: string | null;
  company_id: number | null;
  lifecycle_stage: string | null;
  track: LifecycleTrack | null;
  effective_stale_after: number;
  clock_start: string | null;
  due_at: string | null;
  is_stale: boolean;
  is_paused: boolean;
  is_terminal: boolean;
  commitment_at: string | null;
  commitment_note: string | null;
  /** A DATE (YYYY-MM-DD), inclusive: paused until the 14th is quiet through the 14th. */
  paused_until: string | null;
  paused_reason: string | null;
  last_contacted_at: string | null;
  source: string | null;
  source_detail: string | null;
  open_tasks: number;
  sales_id: number | null;
};

/**
 * A track key. A STRING, not a union, since 084.
 *
 * `track` was a CHECK constraint on three tables and is now a foreign key to
 * `lifecycle_tracks`, precisely so a third track is data rather than a
 * migration. A union here would put the constraint back in the one place that
 * cannot be edited from a settings screen -- and it would typecheck happily
 * while the database accepted a value the app refused to render.
 */
export type LifecycleTrack = string;

/** One row of `public.lifecycle_tracks` (084). */
export type LifecycleTrackRow = {
  /** Virtual, minted from `track` by the provider. See NON_ID_PRIMARY_KEYS. */
  id: string;
  track: LifecycleTrack;
  /** What a person sees. Renamable; the key is not. */
  label: string;
  sort: number;
  /**
   * Preselected when triaging a contact who has no stage yet. At most one row
   * carries it -- see defaultTrack(), which orders rather than searching,
   * because "none set" is a legal state.
   */
  is_default: boolean;
};

/**
 * One row of `public.contact_lifecycle` — the table the panel WRITES.
 *
 * `contact_id` is the primary key and there is NO `id` column, so the
 * supabase data provider is given a primaryKeys entry for this resource;
 * without it every read and write asks PostgREST for a column that does not
 * exist. See NON_ID_PRIMARY_KEYS in ./primaryKeys.ts.
 *
 * A CONTACT MAY HAVE NO ROW AT ALL. `contact_health` left-joins for exactly
 * that reason: an untriaged contact is the population this whole system
 * exists to catch, and an inner-join-shaped assumption anywhere would hide
 * the people who most need chasing. Every reader here must treat the whole
 * row as absent, not merely as null columns.
 */
export type ContactLifecycleRow = {
  /*
   * A VIRTUAL id, equal to contact_id, minted by ra-data-postgrest because we
   * declare the primary key for this resource. react-admin requires every
   * record to have one; the table does not have the column. So it is safe to
   * read and must never be written back -- dataWithoutVirtualId strips it on
   * the way out, which is the other half of why the declaration is needed.
   */
  id: number;
  contact_id: number;
  lifecycle_stage: string | null;
  track: LifecycleTrack | null;
  /** Per-record cadence override. Beats the stage default, which beats 7. */
  stale_after_days: number | null;
  commitment_at: string | null;
  commitment_note: string | null;
  paused_until: string | null;
  paused_reason: string | null;
  /** DERIVED by one database trigger. Never write it from the app. */
  last_contacted_at: string | null;
  source: string | null;
  source_detail: string | null;
  zoho_id: string | null;
};

/**
 * One row of `public.lifecycle_stages`.
 *
 * THE PRIMARY KEY IS (stage, track), NOT stage. `new` and `contacted` exist
 * in both tracks with different cadences — 7 days for a prospect, 30 for a
 * partner — so a stage without its track identifies nothing. Writing one
 * without the other makes contact_health's join find no row, which silently
 * drops the contact onto the 7-day floor.
 *
 * A table rather than an enum on purpose: changing a threshold is data, not
 * a deploy. So the panel reads the list from here and hardcodes none of it.
 */
export type LifecycleStage = {
  /** Virtual, minted from the compound key. See ContactLifecycleRow.id. */
  id: string;
  stage: string;
  track: LifecycleTrack;
  sort: number;
  /** NULL on a terminal stage: there is nothing left to be late for. */
  stale_after_days: number | null;
  /**
   * WHAT THIS STAGE SUGGESTS DOING NEXT — the sentence, the kind of work, and
   * the template (084). All three are null on a terminal stage, and the
   * database refuses otherwise: lifecycle_stages_terminal_suggests_nothing.
   * There is nothing to do next on a stage whose meaning is that there is
   * nothing left to do.
   */
  default_next_action: string | null;
  default_task_type: string | null;
  default_content_id: number | null;
  is_terminal: boolean;
};

/**
 * One row of `public.sequences` — a named cadence of follow-up steps.
 *
 * `track` IS NULLABLE AND NULL MEANS "EITHER TRACK", not "not filled in".
 * `sequences_track_check` allows only NULL, 'prospect' or 'partner', so the
 * column has exactly three states and all three are answers.
 *
 * RETIRED VIA is_active, NEVER DELETED. `enrollments.sequence_id` references
 * this table with no ON DELETE clause at all — a plain restrict — so a delete
 * of a used sequence fails, and a delete of an unused one destroys the record
 * of what somebody was enrolled in the moment it stops being unused. Same
 * reasoning as `content_assets`, which says so on its own screen.
 */
export type Sequence = {
  id: number;
  name: string;
  track: LifecycleTrack | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

/**
 * One row of `public.sequence_steps`.
 *
 * TWO COLUMNS HERE ARE FREE TEXT IN POSTGRES. `channel` and `task_type` have
 * no CHECK constraint of any kind, so the authoring UI is the only thing
 * standing between a typo and a task nobody recognizes — `task_type` is
 * inserted STRAIGHT INTO `tasks.type` by advance_enrollments(). See
 * ./sequenceRules.ts for the vocabularies, and note that the column default
 * ('Follow-up') is not one of them.
 *
 * `offset_days` counts from the PREVIOUS step, not from enrollment. Reading it
 * as days-from-enrollment is wrong by three days on the third step of
 * [0, 3, 4] and the error compounds; stepSchedule() exists to do that sum.
 */
export type SequenceStep = {
  id: number;
  sequence_id: number;
  /** 1-based and REQUIRED to be contiguous — a gap ends enrollments. */
  step_no: number;
  /** Days after the previous step. `>= 0`, enforced in Postgres. */
  offset_days: number;
  channel: string;
  /** NULL is legitimate: a call step needs no template. */
  content_id: number | null;
  /** Written verbatim into `tasks.type`. Must come from the configured list. */
  task_type: string;
};

/**
 * One row of `public.enrollments` — one contact walking one sequence.
 *
 * THREE THINGS ABOUT THIS TABLE DECIDE WHETHER AN ENROLLMENT IS ALIVE, AND
 * NONE OF THEM IS OBVIOUS FROM `status`.
 *
 * 1. WHERE IT HAS GOT TO IS NOT ON THIS ROW. `current_step` and
 *    `next_action_at` were, and both are gone: 077 left them at their
 *    defaults and 078 never wrote either, so anything still reading them
 *    reported "0 done, next is step 1" no matter how many steps had fired.
 *    An enrollment's progress is its `enrollment_steps` -- see EnrollmentStep
 *    below and progress() in ./enrollment.ts. An enrollment with NO steps is
 *    the dead row that used to be a null `next_action_at`: nothing pending,
 *    so the first nightly sweep marks it `completed` and the record claims a
 *    cadence nobody received. 077 refuses to create one.
 *
 * 2. `enrollments_one_active` — UNIQUE (contact_id) WHERE status IN
 *    ('active','paused') — is PARTIAL on purpose: a contact may hold any
 *    number of completed or ended enrollments, so the history is recordable,
 *    but only one live one. The dead row in (1) therefore does not merely do
 *    nothing; it holds the only live slot the contact has and takes them out
 *    of nurture entirely.
 *
 *    THE PREDICATE COVERS `paused` DELIBERATELY (074). A pause that freed the
 *    slot would let the same person be enrolled twice mid-sequence, which is
 *    the protection 052 built from Dittofeed's four-part re-entry rule. So a
 *    paused enrollment is LIVE — see activeEnrollment() in ./enrollment.ts,
 *    which read `status === 'active'` until crm#18 and therefore filed a
 *    suspended enrollment under "everything that is over".
 *
 * 3. `enrollments_ended_has_reason` — CHECK (status IN ('active','paused') OR
 *    ended_reason IS NOT NULL). An ending nobody can account for is the same
 *    defect as a lead that just went quiet, so the database refuses to record
 *    one. Pausing is EXEMPT, and that is the schema saying a pause is not an
 *    ending: it needs no justification because nothing has been given up.
 *
 * `completed` and `ended` are NOT synonyms. The generator writes `completed`
 * with ended_reason `last_step` when there is no next step; a human ending it
 * writes `ended`, and so does the generator when the contact reaches a
 * terminal stage (`stage_terminal`). One finished, the other was stopped.
 *
 * `paused` is neither, and it is the ONE STATUS THAT IS NOT A DESTINATION.
 * advance_enrollments() reads `e.status = 'active'`, so a paused enrollment
 * generates nothing while its pending steps keep their dates — the suspension
 * IS those steps sitting due and unprocessed, not a skip. Resuming shifts
 * them; see shiftAfterPause() in ./enrollmentSchedule.ts.
 */
export type EnrollmentStatus = "active" | "paused" | "completed" | "ended";

/** The two statuses `enrollments_one_active` covers. One contact, one slot. */
export const LIVE_ENROLLMENT_STATUSES = ["active", "paused"] as const;

/** The two terminal statuses. Nothing here is coming back. */
export const OVER_ENROLLMENT_STATUSES = ["completed", "ended"] as const;

export type Enrollment = {
  id: number;
  contact_id: number;
  sequence_id: number;
  status: EnrollmentStatus;
  /** Required whenever the status is neither `active` nor `paused`. */
  ended_reason: string | null;
  ended_at: string | null;
  created_at: string;
  /**
   * When the drip was suspended, and the ONLY record of how long for.
   *
   * Null on every status but `paused` — and null on a `paused` row is a row
   * nothing can honestly resume, because the shift a resume applies is
   * `now - paused_at`. resumeEnrollment() refuses that row rather than
   * guessing a zero shift, which would silently fire every pending step at
   * once on the next run.
   */
  paused_at: string | null;
};

/** The three values `enrollment_steps.status` takes (071). */
export type StepStatus = "pending" | "generated" | "skipped";

/**
 * One row of `public.enrollment_steps` -- what THIS contact will actually
 * receive. See barrel_scout_crm/db/migrations/071, 072 and 075.
 *
 * THE PLAN, NOT THE TEMPLATE, AND THE DIFFERENCE IS THE WHOLE FEATURE. A
 * sequence SEEDS these rows at enroll time and stops being the authority the
 * moment they exist: a step can be added, removed, reordered, retimed or
 * re-pointed for one person without `sequence_steps` moving at all. So the
 * number of steps this enrollment has is the number of THESE rows, and reading
 * the template's length instead is wrong for every contact anybody has edited
 * -- silently, and in the direction that under-reports.
 *
 * `step_no` IS ORDER, NOT POSITION, AND IT IS MUTABLE. 078 walks these rows by
 * `status` and `due_at` and does no arithmetic on the number at all, so a plan
 * numbered 1, 3, 7 is legal and means nothing was lost -- the opposite of
 * `sequence_steps`, where a gap ends an enrollment. Anything deriving "which
 * one is next" from a count plus one is reading the counter 079 deletes.
 *
 * PROGRESS LIVES IN `status`, AND NOWHERE ELSE. `enrollments.current_step` and
 * `enrollments.next_action_at` were the old answer; 077 leaves both at their
 * defaults and 078 never touches either, so a panel that read them reported
 * "0 done, next is step 1" for ever. That is why they are gone from
 * `Enrollment` above, and why they were the last place this shape hid.
 */
export type EnrollmentStep = {
  id: number;
  enrollment_id: number;
  /** Order, MUTABLE, and NOT required to be contiguous. */
  step_no: number;
  /** Days after the PREVIOUS step: the intent `due_at` was computed from. */
  offset_days: number;
  /** The real date, and editable afterwards -- that is the feature. */
  due_at: string;
  /** Null is legitimate: a bare "call them, no script" step. */
  content_id: number | null;
  /** A NULLABLE OVERRIDE since 075. Null means "whatever the asset says". */
  task_type: string | null;
  status: StepStatus;
  /** Required whenever status is `skipped` -- enrollment_steps_skip_has_reason. */
  skipped_reason: string | null;
  /** Whether a person put this step here by hand. Not a propagation filter. */
  source: "template" | "added";
  edited: boolean;
  /**
   * PER-CONTACT CONTENT OVERRIDE (081). Null means "use the asset".
   *
   * These hold the same `{{first_name}}` template syntax `content_assets`
   * holds, NOT rendered text -- see stepContent.ts. They fall back
   * independently, so rewriting only the subject keeps the asset's body.
   * `content_id` still points at the asset after an edit: it records what
   * the step was changed FROM, and it is what propagation matches on.
   */
  subject: string | null;
  body: string | null;
};

/* ── the company profile (069) ─────────────────────────────────────────── */

/** What a company is to us. `kind` rather than `type`, which reads badly in
 *  every query it appears in. NOT the same as ContactLifecycleRow.track: a
 *  track is how a PERSON is being worked; this is what a COMPANY is to us. A
 *  distillery can be a customer while a new contact there is still a prospect.
 *  Do not join them. */
export type CompanyKind = "customer" | "partner" | "other";

/** Only meaningful on a customer — company_profile_customer_type_scope. */
export type CustomerType = "distillery" | "ndp" | "other";

/**
 * What a barrel count COUNTS: barrels held, or barrels a year.
 *
 * A distillery's figure is a lifetime total and an NDP's is annual, and those
 * differ by orders of magnitude — so a count whose basis is only implied is a
 * number nobody can compare, sum or size a deal on. Stored rather than
 * derived because the third customer type, "other", implies no basis at all.
 */
export type BarrelBasis = "total" | "annual";

/**
 * One row of `public.company_profile` — the table the company panel WRITES.
 *
 * `company_id` is the primary key and there is NO `id` column, so the
 * supabase data provider is given a primaryKeys entry for this resource; see
 * NON_ID_PRIMARY_KEYS in ./primaryKeys.ts. Without it every read asks
 * PostgREST for a column that does not exist and every write targets nothing.
 *
 * A COMPANY MAY HAVE NO ROW AT ALL, and today none of the 57 has one. Absence
 * is the normal state, not an error state: every reader must treat the whole
 * row as missing rather than as a row of nulls, and the first write for a
 * company is an INSERT.
 *
 * Deliberately NOT columns on upstream's `companies`. That table is
 * upstream's to reshape (18 commits to CompanyShow.tsx alone in the last 12
 * months), and a column on the record the company form can see is a column
 * that form round-trips whether or not anyone touched it. Same reasoning as
 * contact_lifecycle — see migration 069.
 */
export type CompanyProfileRow = {
  /*
   * A VIRTUAL id, equal to company_id, minted by ra-data-postgrest because we
   * declare the primary key for this resource. react-admin requires every
   * record to have one; the table does not have the column. Safe to read,
   * never to write back.
   */
  id: number;
  company_id: number;
  kind: CompanyKind;
  customer_type: CustomerType | null;
  /** A whole number of barrels, `>= 0`, and NULL unless kind is customer. */
  barrel_count: number | null;
  barrel_count_basis: BarrelBasis | null;
  /** NOT NULL, default true. Estimated is the honest answer, not missing data. */
  barrel_count_is_estimate: boolean;
  notes?: string | null;
};
